import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/hooks";

type ImpersonationContext = {
  startedAt: string;
  expiresAt: number | null;
  impersonatorId: string | null;
};

const STORAGE_KEY = "impersonationContext";

const readContext = (): ImpersonationContext | null => {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as ImpersonationContext;
    if (
      parsed.expiresAt &&
      typeof parsed.expiresAt === "number" &&
      parsed.expiresAt <= Date.now()
    ) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    console.error("Failed to parse impersonation context", error);
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const ImpersonationBanner = () => {
  const { logout, isAuthenticated } = useAuth();
  const [context, setContext] = useState<ImpersonationContext | null>(
    readContext
  );

  useEffect(() => {
    setContext(readContext());
  }, [isAuthenticated]);

  useEffect(() => {
    const syncHandler = () => {
      setContext(readContext());
    };

    window.addEventListener("storage", syncHandler);
    const interval = window.setInterval(syncHandler, 60_000);

    return () => {
      window.removeEventListener("storage", syncHandler);
      window.clearInterval(interval);
    };
  }, []);

  const expiresLabel = useMemo(() => {
    if (!context?.expiresAt) {
      return null;
    }

    return new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(context.expiresAt));
  }, [context]);

  if (!context) {
    return null;
  }

  const handleExit = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    logout();
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900">
      <div>
        <strong>Modo impersonación activo.</strong>{" "}
        {expiresLabel ? `Expira ${expiresLabel}. ` : ""}
        Recuerda cerrar sesión cuando termines.
      </div>
      <button
        onClick={handleExit}
        className="rounded bg-amber-500 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
      >
        Salir
      </button>
    </div>
  );
};

export default ImpersonationBanner;
